import React, { useState } from 'react';
import { Share2, MessageCircle, Facebook, Link2, CheckCircle } from 'lucide-react';
import { publicApi } from '../../../../services/api';
import toast from 'react-hot-toast';

interface SocialShareButtonsProps {
  wheelName: string;
  companySlug: string;
  wheelSlug: string;
  prizeName?: string;
  customerName?: string;
  customerWhatsapp?: string;
}

const SocialShareButtons: React.FC<SocialShareButtonsProps> = ({
  wheelName,
  companySlug,
  wheelSlug,
  prizeName,
  customerName,
  customerWhatsapp,
}) => {
  const [copied, setCopied] = useState(false);

  const wheelUrl = `${window.location.origin}/w/${companySlug}/${wheelSlug}`;
  const shareText = `Acabei de girar a ${wheelName} e ganhei ${prizeName}! Gire você também e concorra a prêmios incríveis!`;

  const recordShare = async (platform: 'whatsapp' | 'facebook' | 'copy_link') => {
    try {
      await publicApi.recordShare(companySlug, wheelSlug, platform, customerName, customerWhatsapp);
    } catch (error) {
      console.error('Erro ao registrar compartilhamento:', error);
      // Não bloqueia o fluxo se falhar
    }
  };

  const handleWhatsAppShare = async () => {
    const message = encodeURIComponent(`${shareText}\n\n${wheelUrl}`);
    await recordShare('whatsapp');
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const handleFacebookShare = async () => {
    await recordShare('facebook');
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(wheelUrl)}`, '_blank');
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(wheelUrl);
      await recordShare('copy_link');
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast.error('Erro ao copiar link');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
        <Share2 className="h-4 w-4" />
        <span>Compartilhe com seus amigos:</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {/* WhatsApp */}
        <button
          onClick={handleWhatsAppShare}
          className="flex flex-col items-center gap-2 p-4 bg-green-50 hover:bg-green-100 border-2 border-green-200 rounded-lg transition-all hover:scale-105 active:scale-95"
        >
          <MessageCircle className="h-6 w-6 text-green-600" />
          <span className="text-xs font-medium text-green-700">WhatsApp</span>
        </button>

        {/* Facebook */}
        <button
          onClick={handleFacebookShare}
          className="flex flex-col items-center gap-2 p-4 bg-blue-50 hover:bg-blue-100 border-2 border-blue-200 rounded-lg transition-all hover:scale-105 active:scale-95"
        >
          <Facebook className="h-6 w-6 text-blue-600" />
          <span className="text-xs font-medium text-blue-700">Facebook</span>
        </button>

        {/* Copiar Link */}
        <button
          onClick={handleCopyLink}
          className={`flex flex-col items-center gap-2 p-4 ${
            copied 
              ? 'bg-emerald-50 border-emerald-200' 
              : 'bg-gray-50 hover:bg-gray-100 border-gray-200'
          } border-2 rounded-lg transition-all hover:scale-105 active:scale-95`}
        >
          {copied ? (
            <CheckCircle className="h-6 w-6 text-emerald-600" />
          ) : (
            <Link2 className="h-6 w-6 text-gray-600" />
          )}
          <span className={`text-xs font-medium ${copied ? 'text-emerald-700' : 'text-gray-700'}`}>
            {copied ? 'Copiado!' : 'Copiar Link'}
          </span>
        </button>
      </div>

      <p className="text-center text-xs text-gray-500 mt-3">
        Quanto mais você compartilhar, mais pessoas poderão ganhar prêmios incríveis! 🎁
      </p>
    </div>
  );
};

export default SocialShareButtons;
